import { useState, useEffect, useCallback } from 'react';
import {
  Wrench,
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader2,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  Bot,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '../../lib/utils';


interface ToolMetricsSummary {
  total_calls: number;
  success_count: number;
  error_count: number;
  success_rate: number;
  avg_duration_ms: number;
  max_duration_ms: number;
  unique_tools: number;
}

interface ToolBreakdown {
  tool_name: string;
  calls: number;
  successes: number;
  errors: number;
  success_rate: number;
  avg_ms: number;
  last_used: string;
}

interface AgentBreakdown {
  agent_id: string;
  agent_name: string;
  calls: number;
  successes: number;
  errors: number;
  avg_ms: number;
}

interface RecentError {
  tool_name: string;
  error_message: string;
  duration_ms: number;
  created_at: string;
  agent_id: string;
}

interface HourlyData {
  hour: string;
  calls: number;
  successes: number;
  errors: number;
}

interface ToolMetricsData {
  summary: ToolMetricsSummary;
  by_tool: ToolBreakdown[];
  by_agent: AgentBreakdown[];
  recent_errors: RecentError[];
  hourly: HourlyData[];
  period_hours: number;
}

export default function ToolMetrics() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ToolMetricsData | null>(null);
  const [period, setPeriod] = useState('24');

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<{ data?: ToolMetricsData }>(`/mcp/tool-metrics?hours=${period}`);
      const raw = (res?.data?.data || res?.data || res) as unknown as ToolMetricsData | null;
      setData(raw);
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [period, toast]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-500 mr-2" />
        <span className="text-sm text-gray-500">Caricamento metriche tool...</span>
      </div>
    );
  }

  const summary = data?.summary || {
    total_calls: 0, success_count: 0, error_count: 0,
    success_rate: 0, avg_duration_ms: 0, max_duration_ms: 0, unique_tools: 0
  };

  const hourlyChartData = (data?.hourly || []).map(h => ({
    name: formatTime(h.hour),
    Successi: h.successes,
    Errori: h.errors,
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Wrench className="h-5 w-5 text-indigo-500" />
          Tool AI — Osservabilità
        </h3>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[130px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Ultima ora</SelectItem>
              <SelectItem value="6">Ultime 6h</SelectItem>
              <SelectItem value="24">Ultime 24h</SelectItem>
              <SelectItem value="168">Ultima settimana</SelectItem>
              <SelectItem value="720">Ultimo mese</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchMetrics} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-2xl font-bold text-indigo-600">{summary.total_calls}</div>
            <div className="text-xs text-gray-500">Tool Calls</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-2xl font-bold text-green-600">{summary.success_rate}%</div>
            <div className="text-xs text-gray-500">Successo</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-3 pb-2">
            <div className={`text-2xl font-bold ${summary.error_count > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {summary.error_count}
            </div>
            <div className="text-xs text-gray-500">Errori</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-2xl font-bold text-blue-600">{summary.unique_tools}</div>
            <div className="text-xs text-gray-500">Tool Attivi</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-2xl font-bold text-amber-600">{formatDuration(summary.avg_duration_ms)}</div>
            <div className="text-xs text-gray-500">Durata Media</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-2xl font-bold text-orange-600">{formatDuration(summary.max_duration_ms)}</div>
            <div className="text-xs text-gray-500">Durata Max</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-2xl font-bold text-purple-600">{summary.success_count}</div>
            <div className="text-xs text-gray-500">Completati</div>
          </CardContent>
        </Card>
      </div>

      {hourlyChartData.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-indigo-500" />
              Andamento Orario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={hourlyChartData} barSize={16}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="Successi" fill="#22c55e" stackId="a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Errori" fill="#ef4444" stackId="a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-indigo-500" />
              Breakdown per Tool
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.by_tool || []).length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nessun tool utilizzato nel periodo</p>
            ) : (
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                {(data?.by_tool || []).map((tool, i) => (
                  <div key={tool.tool_name} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-xs text-gray-400 w-4">{i + 1}</span>
                      <span className="text-sm font-mono truncate" title={tool.tool_name}>
                        {tool.tool_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-2">
                      <span className="text-xs text-gray-500">{tool.calls}x</span>
                      <Badge className={`text-[10px] px-1.5 ${
                        tool.success_rate >= 95 ? 'bg-green-100 text-green-700' :
                        tool.success_rate >= 80 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {tool.success_rate}%
                      </Badge>
                      <span className="text-[10px] text-gray-400 w-12 text-right">{formatDuration(tool.avg_ms)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Bot className="h-4 w-4 text-purple-500" />
              Breakdown per Agente AI
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.by_agent || []).length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nessuna attività agente nel periodo</p>
            ) : (
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                {(data?.by_agent || []).map(agent => (
                  <div key={agent.agent_id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Bot className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                      <span className="text-sm truncate">{agent.agent_name}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-2">
                      <span className="text-xs text-gray-500">{agent.calls} calls</span>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span className="text-[10px] text-green-600">{agent.successes}</span>
                      </div>
                      {agent.errors > 0 && (
                        <div className="flex items-center gap-1">
                          <XCircle className="h-3 w-3 text-red-500" />
                          <span className="text-[10px] text-red-600">{agent.errors}</span>
                        </div>
                      )}
                      <span className="text-[10px] text-gray-400">{formatDuration(agent.avg_ms)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {(data?.recent_errors || []).length > 0 && (
        <Card className="border-red-200 dark:border-red-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-4 w-4" />
              Errori Recenti ({data?.recent_errors.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {(data?.recent_errors || []).map((err, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900">
                  <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono font-medium text-red-700 dark:text-red-400">{err.tool_name}</span>
                      <span className="text-[10px] text-gray-400">{formatDate(err.created_at)}</span>
                      {err.duration_ms > 0 && (
                        <span className="text-[10px] text-gray-400">{formatDuration(err.duration_ms)}</span>
                      )}
                    </div>
                    <p className="text-xs text-red-600 dark:text-red-400 break-all">
                      {(err.error_message || 'Errore sconosciuto').substring(0, 200)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
